import React from "react";
import { NumberInput, Divider } from "@heroui/react";
import { t } from "@lingui/core/macro";

interface DurationInputProps {
  value?: number;
  onValueChange?: (totalMinutes: number) => void;
  label?: React.ReactNode;
  isDisabled?: boolean;
  isRequired?: boolean;
  className?: string;
}

interface DurationValues {
  days: number;
  hours: number;
  minutes: number;
}

export const DurationInput: React.FC<DurationInputProps> = ({
  value = 0,
  onValueChange,
  label,
  isDisabled = false,
  isRequired = false,
  className = "",
}) => {
  // Convert total minutes to days, hours, minutes
  const calculateDurationValues = (totalMinutes: number): DurationValues => {
    const days = Math.floor(totalMinutes / (24 * 60));
    const remainingMinutes = totalMinutes % (24 * 60);
    const hours = Math.floor(remainingMinutes / 60);
    const minutes = remainingMinutes % 60;

    return { days, hours, minutes };
  };

  // Calculate total minutes from days, hours, minutes
  const calculateTotalMinutes = (values: DurationValues): number => {
    return values.days * 24 * 60 + values.hours * 60 + values.minutes;
  };

  const [durationValues, setDurationValues] = React.useState<DurationValues>(
    calculateDurationValues(value),
  );

  // Update internal state when external value changes
  React.useEffect(() => {
    setDurationValues(calculateDurationValues(value));
  }, [value]);

  // Handle individual field changes
  const handleFieldChange = (field: keyof DurationValues, newValue: number) => {
    // Ensure value is not negative
    const validValue = Math.max(0, newValue);

    const updatedValues = {
      ...durationValues,
      [field]: validValue,
    };

    setDurationValues(updatedValues);

    // Notify parent component about the change
    onValueChange?.(calculateTotalMinutes(updatedValues));
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <div>
          <label className="text-xs text-default-500">
            {label}
            {isRequired && <span className="ml-1 text-danger">*</span>}
          </label>
        </div>
      )}
      <div className="rounded-xl border-1 border-default-200 p-1">
        <div className="flex flex-col gap-0 sm:flex-row">
          <div className="flex-1">
            <NumberInput
              label={t`Dni`}
              value={durationValues.days}
              onValueChange={(value) => handleFieldChange("days", value)}
              minValue={0}
              step={1}
              isDisabled={isDisabled}
              className="w-full"
              size="sm"
              classNames={{
                inputWrapper: "rounded-tr-none rounded-br-none",
                label: "text-xs",
              }}
            />
          </div>

          <Divider orientation="vertical" className="hidden sm:block" />
          <Divider className="sm:hidden" />

          <div className="flex-1">
            <NumberInput
              label={t`Ur`}
              value={durationValues.hours}
              onValueChange={(value) => handleFieldChange("hours", value)}
              minValue={0}
              maxValue={23}
              step={1}
              isDisabled={isDisabled}
              className="w-full"
              size="sm"
              classNames={{
                inputWrapper: "rounded-none",
                label: "text-xs",
              }}
            />
          </div>

          <Divider orientation="vertical" className="hidden sm:block" />
          <Divider className="sm:hidden" />

          <div className="flex-1">
            <NumberInput
              label={t`Minut`}
              value={durationValues.minutes}
              onValueChange={(value) => handleFieldChange("minutes", value)}
              minValue={0}
              maxValue={59}
              step={5}
              isDisabled={isDisabled}
              className="w-full"
              size="sm"
              classNames={{
                inputWrapper: "rounded-tl-none rounded-bl-none",
                label: "text-xs",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
